// ============================================================================
// PHI SCAN UTILITY (BLOCK 13 - Pilot Mode Runtime Safety)
// UPDATED: Context-aware false-positive reduction + dismissal support
// ============================================================================

/**
 * PHI Detection Heuristics with Context-Aware False-Positive Reduction
 *
 * Scans text for patterns that may indicate Protected Health Information (PHI)
 * Returns array of detection reasons with severity levels without storing raw text
 *
 * DETECTION REASONS:
 *   - email_detected: Email address pattern
 *   - phone_detected: Phone number pattern
 *   - mrn_keyword: Medical Record Number keyword
 *   - dob_keyword: Date of Birth keyword
 *   - address_keyword: Address components
 *   - long_number: Suspicious long number sequences
 *   - date_pattern: Date patterns (potential DOB)
 *
 * SEVERITY LEVELS:
 *   - "block": High confidence PHI, should block submission until dismissed
 *   - "warning": Lower confidence, may be clinical context (timeline/dosage)
 */

// Email pattern (basic)
const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;

// Phone patterns (US / international). One unified 10-digit matcher covering:
//   555-123-4567 · 555.123.4567 · 5551234567 · 555 123 4567
//   (555) 123-4567 · (555)123-4567 · +1 555 123 4567 · +1 (555) 123-4567
// Structure: optional +country prefix, area code optionally parenthesized, then
// 3-3-4 digit groups with separators space/dot/dash/none.
//
// Anchors are non-digit lookarounds (?<!\d) … (?!\d) — NOT \b — because \b\(
// never matches a LEADING parenthesis (both space and '(' are non-word chars, so
// no boundary exists), which is exactly why "(555) 123-4567" was previously
// missed. The lookarounds still prevent grabbing a phone from inside a longer
// numeric run (lab IDs / MRNs).
//
// Separators are restricted to [ .-] (NOT \s) so slashed/colon clinical values
// like dates "12/31/1990" and "120/80" never read as phones, and a single
// optional separator per gap avoids matching loosely-spaced number lists.
const PHONE_PATTERNS = [
  /(?<!\d)(?:\+\d{1,3}[ .-]?)?(?:\(\d{3}\)|\d{3})[ .-]?\d{3}[ .-]?\d{4}(?!\d)/,
];

// MRN keywords (case-insensitive)
const MRN_KEYWORDS = [
  /\b(mrn|medical\s+record\s+number)\b/i,
  /\b(patient\s+id|patient\s+number)\b/i,
  /\b(chart\s+number)\b/i,
];

// DOB keywords (case-insensitive)
const DOB_KEYWORDS = [
  /\b(date\s+of\s+birth|dob)\b/i,
  /\bborn\s+on\b/i,
  /\bbirthday\b/i,
];

// Address keywords (case-insensitive)
const ADDRESS_KEYWORDS = [
  /\b(street|avenue|road|lane|drive|blvd|boulevard)\b/i,
  /\b(apt|apartment|unit|suite)\b/i,
  /\b\d{5}(-\d{4})?\b/,  // ZIP code pattern
  /\b(address|residential)\b/i,
];

// Long number patterns (SSN, MRN, etc.)
const LONG_NUMBER_PATTERN = /\b\d{6,}\b/;  // 6+ consecutive digits

// Date patterns (potential DOB)
const DATE_PATTERNS = [
  /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/,    // 12/31/1990, 12-31-90
  /\b\d{4}[/-]\d{1,2}[/-]\d{1,2}\b/,      // 1990/12/31, 1990-12-31
  /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2},?\s+\d{4}\b/i,  // January 1, 1990
];

// ============================================================================
// CONTEXT-AWARE FALSE-POSITIVE REDUCTION (NEW)
// ============================================================================

/**
 * Clinical timeline context patterns (suppress date/phone false positives)
 * If date/number appears in these contexts, it's likely a clinical timeline, not PHI
 */
const CLINICAL_TIMELINE_PATTERNS = [
  // Timeline references
  /\b(symptoms?\s+(started|began|onset|since|for|duration))\b/i,
  /\b(since|for|duration|over|past)\s+\d+\s*(day|week|month|year)s?\b/i,
  /\b(call|follow\s*up|return|see\s+me)\s+(in|within)\s+\d+[-/]\d+\s*(day|week|month)s?\b/i,
  /\b\d+[-/]\d+\s*(day|week|month|year)s?\s+(ago|later|prior)\b/i,

  // Clinical dosage/timing
  /\b(every|q|x)\s*\d+[-/]\d+\s*(hour|day|week)s?\b/i,
  /\b\d+[-/]\d+\s*(times?|x)\s+(daily|weekly|monthly|per\s+day)\b/i,

  // Visit scheduling (not actual phone numbers)
  /\b(schedule|appointment|visit)\s+in\s+\d+[-/]\d+\s*(day|week)s?\b/i,
];

/**
 * Dosage/lab value context patterns (suppress long_number false positives)
 * If long number appears in these contexts, it's likely a dosage/value, not SSN/MRN
 */
const DOSAGE_LAB_CONTEXT_PATTERNS = [
  /\b(dose|dosage|mg|mcg|ml|units?|iu)\b/i,
  /\b(lab|result|value|level|count|wbc|rbc|platelet)\b/i,
  /\b(creatinine|glucose|sodium|potassium|hemoglobin|hematocrit)\b/i,
  /\b\d{6,}\s*(mg|mcg|ml|units?|iu|cells?)\b/i,
];

/**
 * Check if text contains clinical timeline context
 * @param {string} text - Text to check
 * @returns {boolean} True if clinical timeline context detected
 */
const hasClinicalTimelineContext = (text) => {
  return CLINICAL_TIMELINE_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * Check if text contains dosage/lab context
 * @param {string} text - Text to check
 * @returns {boolean} True if dosage/lab context detected
 */
const hasDosageLabContext = (text) => {
  return DOSAGE_LAB_CONTEXT_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * Scan text for PHI patterns with context-aware severity
 *
 * @param {string} text - Text to scan
 * @returns {Object} { detected: boolean, reasons: Array<{reason: string, severity: 'block'|'warning'}> }
 */
export const scanForPhi = (text) => {
  if (!text || typeof text !== 'string' || text.trim() === '') {
    return { detected: false, reasons: [] };
  }

  const reasons = [];
  const lowerText = text.toLowerCase();

  // Context flags (computed once)
  const hasTimelineContext = hasClinicalTimelineContext(text);
  const hasDosageContext = hasDosageLabContext(text);

  // Email detection (always block - no clinical context for emails)
  if (EMAIL_PATTERN.test(text)) {
    reasons.push({ reason: 'email_detected', severity: 'block' });
  }

  // Phone detection (context-aware)
  if (PHONE_PATTERNS.some(pattern => pattern.test(text))) {
    // If appears in timeline context (e.g., "call in 3-5 days"), downgrade to warning
    const severity = hasTimelineContext ? 'warning' : 'block';
    reasons.push({ reason: 'phone_detected', severity });
  }

  // MRN keyword detection (always block)
  if (MRN_KEYWORDS.some(pattern => pattern.test(text))) {
    reasons.push({ reason: 'mrn_keyword', severity: 'block' });
  }

  // DOB keyword detection (always block)
  if (DOB_KEYWORDS.some(pattern => pattern.test(text))) {
    reasons.push({ reason: 'dob_keyword', severity: 'block' });
  }

  // Address keyword detection (always block)
  if (ADDRESS_KEYWORDS.some(pattern => pattern.test(text))) {
    reasons.push({ reason: 'address_keyword', severity: 'block' });
  }

  // Long number detection (context-aware)
  if (LONG_NUMBER_PATTERN.test(text)) {
    // If appears in dosage/lab context, downgrade to warning
    const severity = hasDosageContext ? 'warning' : 'block';
    reasons.push({ reason: 'long_number', severity });
  }

  // Date pattern detection (context-aware)
  if (DATE_PATTERNS.some(pattern => pattern.test(text))) {
    // If appears in timeline context (e.g., "symptoms started 12/15"), downgrade to warning
    const severity = hasTimelineContext ? 'warning' : 'block';
    reasons.push({ reason: 'date_pattern', severity });
  }

  return {
    detected: reasons.length > 0,
    reasons,
  };
};

// ============================================================================
// STORAGE REDACTION (S1 — Never-PHI write hardening)
//
// redactBlockingPhi removes high-confidence PHI VALUE patterns (email, phone,
// SSN/MRN-like long numbers, DOB-like dates) from free text BEFORE it is written
// to a Never-PHI sink (referral root, audit logs, sessionStorage). It mirrors
// scanForPhi's BLOCK severity exactly:
//   - Context-aware: clinical timeline / dosage matches are WARNING severity and
//     are intentionally PRESERVED (e.g. "glucose 120000", "symptoms since 12/15").
//   - Keyword-only signals (mrn/dob/address keywords) and bare names are NOT
//     redacted here — they have no reliable value span and over-redaction would
//     gut clinical text. They remain governed by the UI warn+attest model.
//     (Documented S1 limitation: regex redaction cannot catch free-form
//      names/addresses without a value pattern.)
// Redaction is idempotent: a '[redacted-*]' token contains no PHI pattern, so
// re-running redactBlockingPhi on its own output is a no-op.
// ============================================================================

const REDACTION_TOKENS = {
  email: '[redacted-email]',
  phone: '[redacted-phone]',
  date: '[redacted-date]',
  number: '[redacted-number]',
};

// Build a global-flagged clone of a pattern for replace(), preserving 'i'.
const globalize = (re) => new RegExp(re.source, re.flags.includes('i') ? 'gi' : 'g');

/**
 * Redact block-severity PHI value patterns from a single string.
 *
 * @param {string} text
 * @returns {{ text: string, changed: boolean, reasons: string[] }}
 */
export const redactBlockingPhi = (text) => {
  if (!text || typeof text !== 'string' || text.trim() === '') {
    return { text, changed: false, reasons: [] };
  }

  const hasTimelineContext = hasClinicalTimelineContext(text);
  const hasDosageContext = hasDosageLabContext(text);
  let out = text;
  const reasons = [];

  // Email — always block (no clinical context for emails). Redact first so a
  // 10-digit-looking phone inside an email is not double-processed.
  if (EMAIL_PATTERN.test(out)) {
    out = out.replace(globalize(EMAIL_PATTERN), REDACTION_TOKENS.email);
    reasons.push('email_detected');
  }

  // Phone — block unless clinical timeline context (e.g. "call in 3-5 days").
  if (!hasTimelineContext) {
    for (const pattern of PHONE_PATTERNS) {
      if (pattern.test(out)) {
        out = out.replace(globalize(pattern), REDACTION_TOKENS.phone);
        reasons.push('phone_detected');
      }
    }
  }

  // Date — block unless clinical timeline context (e.g. "symptoms started 12/15").
  if (!hasTimelineContext) {
    for (const pattern of DATE_PATTERNS) {
      if (pattern.test(out)) {
        out = out.replace(globalize(pattern), REDACTION_TOKENS.date);
        reasons.push('date_pattern');
      }
    }
  }

  // Long number (SSN/MRN-like) — block unless dosage/lab context (e.g. "glucose 120000").
  if (!hasDosageContext && LONG_NUMBER_PATTERN.test(out)) {
    out = out.replace(globalize(LONG_NUMBER_PATTERN), REDACTION_TOKENS.number);
    reasons.push('long_number');
  }

  return { text: out, changed: out !== text, reasons: [...new Set(reasons)] };
};

/**
 * True if the string still contains a high-confidence PHI VALUE pattern that
 * redactBlockingPhi would remove. Used as the post-sanitization fail-fast check
 * (it intentionally ignores keyword-only signals, which are not value-redactable).
 *
 * @param {string} text
 * @returns {boolean}
 */
export const containsBlockingValuePhi = (text) => {
  if (!text || typeof text !== 'string' || text.trim() === '') return false;
  const hasTimelineContext = hasClinicalTimelineContext(text);
  const hasDosageContext = hasDosageLabContext(text);
  if (EMAIL_PATTERN.test(text)) return true;
  if (!hasTimelineContext && PHONE_PATTERNS.some((p) => p.test(text))) return true;
  if (!hasTimelineContext && DATE_PATTERNS.some((p) => p.test(text))) return true;
  if (!hasDosageContext && LONG_NUMBER_PATTERN.test(text)) return true;
  return false;
};

/**
 * Deep, immutable redaction of block-severity PHI from any answer value shape
 * (string / array / plain object). Numbers/booleans/null pass through unchanged.
 * Returns a NEW structure — never mutates the input.
 *
 * @param {*} value
 * @returns {*}
 */
export const redactBlockingPhiDeep = (value) => {
  if (typeof value === 'string') return redactBlockingPhi(value).text;
  if (Array.isArray(value)) return value.map(redactBlockingPhiDeep);
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) out[key] = redactBlockingPhiDeep(value[key]);
    return out;
  }
  return value;
};

/**
 * Deep check: true if any string anywhere in the value still contains a
 * block-severity PHI VALUE pattern.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const containsBlockingValuePhiDeep = (value) => {
  if (typeof value === 'string') return containsBlockingValuePhi(value);
  if (Array.isArray(value)) return value.some(containsBlockingValuePhiDeep);
  if (value && typeof value === 'object') {
    return Object.values(value).some(containsBlockingValuePhiDeep);
  }
  return false;
};

/**
 * Get human-readable explanation for detection reasons
 *
 * @param {Array<{reason: string, severity: string}>} reasons - Array of detection reasons with severity
 * @returns {string[]} Array of human-readable explanations
 */
export const getPhiWarningMessages = (reasons) => {
  const messages = {
    email_detected: 'Email address detected',
    phone_detected: 'Phone number detected',
    mrn_keyword: 'Medical record number reference detected',
    dob_keyword: 'Date of birth reference detected',
    address_keyword: 'Address information detected',
    long_number: 'Long number sequence detected (may be SSN/MRN)',
    date_pattern: 'Date pattern detected (may be date of birth)',
  };

  return reasons.map(item => {
    const reasonKey = typeof item === 'string' ? item : item.reason;
    const severity = typeof item === 'object' && item.severity ? ` (${item.severity})` : '';
    return (messages[reasonKey] || reasonKey) + severity;
  });
};

/**
 * Check if any reasons are severity="block" (requires dismissal or fix)
 *
 * @param {Array<{reason: string, severity: string}>} reasons - Array of detection reasons
 * @returns {boolean} True if any reason has severity="block"
 */
export const hasBlockingReasons = (reasons) => {
  return reasons.some(item => item.severity === 'block');
};

/**
 * Generate a stable hash signature for dismissed PHI warning
 * Uses fieldId + detected reasons (no raw text stored)
 *
 * @param {string} fieldId - Field ID where PHI was detected
 * @param {Array<{reason: string}>} reasons - Detection reasons
 * @returns {string} Hash signature (deterministic)
 */
export const generatePhiSignature = (fieldId, reasons) => {
  if (!fieldId || !reasons || reasons.length === 0) {
    throw new Error('fieldId and reasons are required for PHI signature');
  }

  // Sort reasons to ensure deterministic hash
  const reasonKeys = reasons.map(r => r.reason).sort();
  const signatureString = `${fieldId}:${reasonKeys.join(',')}`;

  // Simple hash function (browser-safe, no crypto needed for this use case)
  let hash = 0;
  for (let i = 0; i < signatureString.length; i++) {
    const char = signatureString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return `phi_${Math.abs(hash).toString(16)}`;
};

/**
 * Scan multiple fields for PHI
 *
 * @param {Object} answers - Answers object { fieldId: value }
 * @param {Array} textareaFieldIds - Array of field IDs that are textarea type
 * @returns {Object} { detected: boolean, warnings: { fieldId: { detected: boolean, reasons: Array<{reason, severity}> } } }
 */
export const scanAnswersForPhi = (answers, textareaFieldIds = []) => {
  if (!answers || typeof answers !== 'object') {
    return { detected: false, warnings: {} };
  }

  const warnings = {};
  let anyDetected = false;

  textareaFieldIds.forEach((fieldId) => {
    const value = answers[fieldId];

    if (value && typeof value === 'string') {
      const result = scanForPhi(value);

      if (result.detected) {
        warnings[fieldId] = result;
        anyDetected = true;
      }
    }
  });

  return {
    detected: anyDetected,
    warnings,
  };
};

// ============================================================================
// DISMISSAL REASON ENUM (Required for audit trail)
// ============================================================================

export const PHI_DISMISSAL_REASONS = {
  CLINICAL_TIMELINE: 'clinical_timeline',
  DOSAGE_OR_LAB_VALUE: 'dosage_or_lab_value',
  CLINICAL_DATE_NOT_DOB: 'clinical_date_not_dob',
  MEASUREMENT_NOT_PHONE: 'measurement_not_phone',
  FALSE_POSITIVE_OTHER: 'false_positive_other',
};

export const PHI_DISMISSAL_REASON_LABELS = {
  [PHI_DISMISSAL_REASONS.CLINICAL_TIMELINE]: 'Clinical timeline (e.g., "symptoms started 12/15")',
  [PHI_DISMISSAL_REASONS.DOSAGE_OR_LAB_VALUE]: 'Dosage or lab value (e.g., "glucose 120000")',
  [PHI_DISMISSAL_REASONS.CLINICAL_DATE_NOT_DOB]: 'Clinical date, not date of birth',
  [PHI_DISMISSAL_REASONS.MEASUREMENT_NOT_PHONE]: 'Measurement/range, not phone number (e.g., "3-5 days")',
  [PHI_DISMISSAL_REASONS.FALSE_POSITIVE_OTHER]: 'Other false positive',
};

export default {
  scanForPhi,
  getPhiWarningMessages,
  hasBlockingReasons,
  generatePhiSignature,
  scanAnswersForPhi,
  redactBlockingPhi,
  containsBlockingValuePhi,
  redactBlockingPhiDeep,
  containsBlockingValuePhiDeep,
  PHI_DISMISSAL_REASONS,
  PHI_DISMISSAL_REASON_LABELS,
};
